import { useState, useMemo } from "react";
import QRCode from "react-qr-code";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatMoney } from "@/lib/eventTypes";
import { CheckCircle2, Ticket as TicketIcon, ShoppingBag as StoreIcon } from "lucide-react";

// ============== FORM ==============
export function FormEventPublic({ event }) {
  const [values, setValues] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const setVal = (id, v) => setValues({ ...values, [id]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Escribe tu nombre");
    setLoading(true);
    try {
      await api.post(`/events/${event.slug}/submit`, {
        name, email: email || null, phone,
        form_values: values,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent)
    return <SuccessCard message="¡Gracias! Recibimos tu respuesta." />;

  return (
    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="form-event">
      <NameEmailPhone name={name} setName={setName} email={email} setEmail={setEmail} phone={phone} setPhone={setPhone} />
      {(event.form_fields || []).map((f) => (
        <div key={f.id} className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">
            {f.label} {f.required && <span className="text-coral">*</span>}
          </Label>
          <RenderField f={f} value={values[f.id]} onChange={(v) => setVal(f.id, v)} />
        </div>
      ))}
      <Button type="submit" disabled={loading} className="rounded-full bg-coral hover:bg-coral-hover text-white h-11 px-6" data-testid="form-submit-btn">
        {loading ? "Enviando…" : "Enviar"}
      </Button>
    </form>
  );
}

function RenderField({ f, value, onChange }) {
  const testid = `form-field-${f.id}`;
  const commonProps = { value: value || "", onChange: (e) => onChange(e.target.value), required: f.required, "data-testid": testid };
  if (f.type === "textarea") return <Textarea {...commonProps} className="rounded-xl border-line min-h-[90px]" />;
  if (f.type === "select" || f.type === "radio") {
    if (f.type === "radio") {
      return (
        <div className="space-y-1.5">
          {(f.options || []).map((op) => (
            <label key={op} className="flex items-center gap-2">
              <input type="radio" name={f.id} checked={value === op} onChange={() => onChange(op)} required={f.required} data-testid={`${testid}-${op}`} />
              <span className="text-sm text-ink-700">{op}</span>
            </label>
          ))}
        </div>
      );
    }
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger className="rounded-xl border-line h-11" data-testid={testid}><SelectValue placeholder="Selecciona…" /></SelectTrigger>
        <SelectContent>
          {(f.options || []).map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === "checkbox") {
    return (
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={value === "on"} onChange={(e) => onChange(e.target.checked ? "on" : "")} data-testid={testid} />
        <span className="text-sm text-ink-700">{f.placeholder || f.label}</span>
      </label>
    );
  }
  return <Input type={f.type === "email" ? "email" : f.type === "tel" ? "tel" : f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} placeholder={f.placeholder} {...commonProps} className="rounded-xl border-line h-11" />;
}

// ============== GIFT / FUNDRAISER ==============
export function GoalEventPublic({ event, onDone }) {
  const isGift = event.event_type === "gift";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const goal = event.goal || { target_amount: 0, currency: "MXN", subject: "" };
  const raised = event.total_collected || 0;
  const pct = goal.target_amount > 0 ? Math.min(100, Math.round((raised / goal.target_amount) * 100)) : 0;

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!name.trim()) return toast.error("Escribe tu nombre");
    if (!amt || amt <= 0) return toast.error("Ingresa una cantidad válida");
    setLoading(true);
    try {
      await api.post(`/events/${event.slug}/submit`, { name, email: email || null, phone, amount: amt, note, anonymous });
      setSent(true);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 space-y-6">
      <div className="bg-white border border-line rounded-2xl p-6">
        <div className="flex flex-wrap items-baseline gap-3">
          <span className="font-heading text-3xl text-ink-900">{formatMoney(raised, goal.currency)}</span>
          <span className="text-ink-500 text-sm">de {formatMoney(goal.target_amount, goal.currency)}</span>
        </div>
        <div className="mt-4 h-2 rounded-full bg-cream-100 overflow-hidden">
          <div className="h-full bg-coral transition-all" style={{ width: `${pct}%` }} data-testid="goal-progress" />
        </div>
        <div className="mt-2 text-xs text-ink-500">
          {pct}% completado · {event.submissions_count || 0} aportaciones
          {goal.subject && <> · <b className="text-ink-700">{isGift ? "Para" : "Para"}: {goal.subject}</b></>}
        </div>
      </div>

      {sent ? (
        <SuccessCard message="¡Gracias por tu aportación!" />
      ) : (
        <form onSubmit={submit} className="bg-white border border-line rounded-2xl p-6 space-y-4" data-testid="goal-form">
          <h3 className="font-heading text-xl text-ink-900">
            {isGift ? "Aporta a la vaquita" : "Hacer una donación"}
          </h3>
          <NameEmailPhone name={name} setName={setName} email={email} setEmail={setEmail} phone={phone} setPhone={setPhone} />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Cantidad</Label>
              <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="500" className="rounded-xl border-line h-11 mt-1" data-testid="goal-amount-input" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-ink-500">Moneda</Label>
              <Input value={goal.currency} readOnly className="rounded-xl border-line h-11 mt-1 bg-cream-50" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-ink-500">Mensaje (opcional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl border-line min-h-[70px] mt-1" data-testid="goal-note-input" />
          </div>
          {!isGift && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="goal-anonymous" />
              <span className="text-sm text-ink-700">Donar de forma anónima</span>
            </label>
          )}
          <PaymentNotice event={event} />
          <Button type="submit" disabled={loading} className="rounded-full bg-coral hover:bg-coral-hover text-white h-11 px-6" data-testid="goal-submit-btn">
            {loading ? "Enviando…" : "Confirmar aportación"}
          </Button>
        </form>
      )}
    </div>
  );
}

// ============== TICKETS ==============
export function TicketsEventPublic({ event }) {
  const [selectedType, setSelectedType] = useState((event.ticket_types || [])[0]?.id || "");
  const [quantity, setQuantity] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Escribe tu nombre");
    if (!selectedType) return toast.error("Elige un tipo de boleto");
    setLoading(true);
    try {
      const { data } = await api.post(`/events/${event.slug}/submit`, {
        name, email: email || null, phone,
        ticket_type_id: selectedType, quantity: parseInt(quantity, 10) || 1,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return <TicketsSuccess submission={result} event={event} />;
  }

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-3">
        <h3 className="font-heading text-xl text-ink-900">Elige tu boleto</h3>
        {(event.ticket_types || []).map((t) => {
          const remaining = t.quantity - (t.sold || 0);
          const sold_out = remaining <= 0;
          const isSel = selectedType === t.id;
          return (
            <button
              key={t.id}
              onClick={() => !sold_out && setSelectedType(t.id)}
              disabled={sold_out}
              className={`w-full text-left border rounded-2xl p-4 transition-colors ${isSel ? "border-coral bg-coral/5" : "border-line bg-white"} ${sold_out ? "opacity-50 cursor-not-allowed" : "hover:border-coral cursor-pointer"}`}
              data-testid={`ticket-option-${t.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-lg text-ink-900">{t.name}</div>
                  {t.description && <div className="text-sm text-ink-500 mt-1">{t.description}</div>}
                  <div className="text-xs text-ink-500 mt-2">{sold_out ? "Agotado" : `${remaining} disponibles`}</div>
                </div>
                <div className="text-coral font-heading text-xl">{formatMoney(t.price, t.currency)}</div>
              </div>
            </button>
          );
        })}
      </div>
      <form onSubmit={submit} className="bg-white border border-line rounded-2xl p-6 space-y-4 self-start" data-testid="tickets-form">
        <h3 className="font-heading text-xl text-ink-900">Datos del comprador</h3>
        <NameEmailPhone name={name} setName={setName} email={email} setEmail={setEmail} phone={phone} setPhone={setPhone} />
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Cantidad</Label>
          <Input type="number" min={1} max={20} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="rounded-xl border-line h-11 mt-1" data-testid="tickets-qty" />
        </div>
        <PaymentNotice event={event} />
        <Button type="submit" disabled={loading} className="rounded-full bg-coral hover:bg-coral-hover text-white h-11 px-6" data-testid="tickets-submit-btn">
          <TicketIcon size={16} strokeWidth={1.75} className="mr-2" />
          {loading ? "Reservando…" : "Reservar boletos"}
        </Button>
      </form>
    </div>
  );
}

function TicketsSuccess({ submission, event }) {
  return (
    <div className="mt-8 space-y-6">
      <SuccessCard message={`Tus ${submission.qr_codes?.length || 1} boleto(s) están confirmados. Guarda esta pantalla o toma captura — muéstrala en la entrada.`} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {submission.qr_codes.map((q, i) => (
          <div key={q} className="bg-white border border-line rounded-2xl p-6 flex flex-col items-center" data-testid={`ticket-qr-${i}`}>
            <div className="text-xs uppercase tracking-widest text-ink-500">Boleto {i + 1} de {submission.qr_codes.length}</div>
            <div className="my-4 bg-white p-3">
              <QRCode value={q} size={140} />
            </div>
            <div className="text-sm text-ink-900 font-medium">{submission.name}</div>
            <div className="text-xs text-ink-500">{submission.ticket_type_name}</div>
            <div className="mt-1 text-xs text-ink-300 font-mono">{q.slice(0, 12)}…</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== DUES ==============
export function DuesEventPublic({ event }) {
  const cfg = event.dues_config || {};
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState(cfg.amount || 0);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Escribe tu nombre");
    setLoading(true);
    try {
      await api.post(`/events/${event.slug}/submit`, {
        name, email: email || null, phone,
        amount: cfg.allow_partial ? parseFloat(amount) : cfg.amount,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) return <SuccessCard message="¡Registrado! El organizador confirmará tu pago." />;

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white border border-line rounded-2xl p-6">
        <div className="text-xs uppercase tracking-widest text-ink-500">Cuota</div>
        <div className="mt-1 font-heading text-3xl text-ink-900">{formatMoney(cfg.amount, cfg.currency)}</div>
        {cfg.deadline && <div className="mt-2 text-sm text-ink-500">Fecha límite: {cfg.deadline}</div>}
        {cfg.notes && <div className="mt-4 text-sm text-ink-700 whitespace-pre-line">{cfg.notes}</div>}
      </div>
      <form onSubmit={submit} className="bg-white border border-line rounded-2xl p-6 space-y-4" data-testid="dues-form">
        <h3 className="font-heading text-xl text-ink-900">Registra tu pago</h3>
        <NameEmailPhone name={name} setName={setName} email={email} setEmail={setEmail} phone={phone} setPhone={setPhone} />
        {cfg.allow_partial && (
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-ink-500">Monto a pagar</Label>
            <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl border-line h-11 mt-1" data-testid="dues-amount-input" />
          </div>
        )}
        <PaymentNotice event={event} />
        <Button type="submit" disabled={loading} className="rounded-full bg-coral hover:bg-coral-hover text-white h-11 px-6" data-testid="dues-submit-btn">
          {loading ? "Enviando…" : "Registrar pago"}
        </Button>
      </form>
    </div>
  );
}

// ============== STORE ==============
export function StoreEventPublic({ event }) {
  const [cart, setCart] = useState({}); // product_id -> qty
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const products = event.store_products || [];
  const total = useMemo(() => {
    let t = 0;
    for (const p of products) {
      const q = cart[p.id] || 0;
      t += q * p.price;
    }
    return t;
  }, [cart, products]);

  const currency = products[0]?.currency || "MXN";

  const inc = (pid) => setCart({ ...cart, [pid]: (cart[pid] || 0) + 1 });
  const dec = (pid) => setCart({ ...cart, [pid]: Math.max(0, (cart[pid] || 0) - 1) });

  const submit = async () => {
    if (!name.trim()) return toast.error("Escribe tu nombre");
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([product_id, quantity]) => ({ product_id, quantity }));
    if (items.length === 0) return toast.error("Elige al menos un producto");
    setLoading(true);
    try {
      await api.post(`/events/${event.slug}/submit`, { name, email: email || null, phone, order_items: items });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) return <SuccessCard message="¡Pedido registrado! El organizador te contactará para entregar tu compra." />;

  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2 space-y-3">
        <h3 className="font-heading text-xl text-ink-900">Productos</h3>
        {products.map((p) => {
          const remaining = p.quantity === 0 ? Infinity : p.quantity - (p.sold || 0);
          const q = cart[p.id] || 0;
          const sold_out = remaining <= 0;
          return (
            <div key={p.id} className="bg-white border border-line rounded-2xl p-4 flex items-center gap-4" data-testid={`product-${p.id}`}>
              <div className="flex-1">
                <div className="font-heading text-lg text-ink-900">{p.name}</div>
                {p.description && <div className="text-sm text-ink-500 mt-0.5">{p.description}</div>}
                <div className="text-xs text-ink-500 mt-1">
                  {formatMoney(p.price, p.currency)} · {sold_out ? "Agotado" : p.quantity === 0 ? "Ilimitado" : `${remaining} disponibles`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="rounded-full border-line w-8 h-8 p-0" onClick={() => dec(p.id)} disabled={q === 0} data-testid={`product-dec-${p.id}`}>-</Button>
                <span className="w-6 text-center font-medium" data-testid={`product-qty-${p.id}`}>{q}</span>
                <Button size="sm" variant="outline" className="rounded-full border-line w-8 h-8 p-0" onClick={() => inc(p.id)} disabled={sold_out || q >= remaining} data-testid={`product-inc-${p.id}`}>+</Button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="bg-white border border-line rounded-2xl p-6 space-y-4 self-start">
        <h3 className="font-heading text-xl text-ink-900">Tu pedido</h3>
        <div className="text-3xl font-heading text-coral" data-testid="cart-total">{formatMoney(total, currency)}</div>
        <NameEmailPhone name={name} setName={setName} email={email} setEmail={setEmail} phone={phone} setPhone={setPhone} />
        <PaymentNotice event={event} />
        <Button onClick={submit} disabled={loading || total === 0} className="w-full rounded-full bg-coral hover:bg-coral-hover text-white h-11" data-testid="store-checkout-btn">
          <StoreIcon size={16} strokeWidth={1.75} className="mr-2" />
          {loading ? "Enviando…" : "Confirmar pedido"}
        </Button>
      </div>
    </div>
  );
}

// ============== WAIVER ==============
export function WaiverEventPublic({ event }) {
  const cfg = event.waiver_config || { waiver_text: "" };
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [typedSignature, setTypedSignature] = useState("");
  const [relation, setRelation] = useState("self");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Escribe tu nombre");
    if (!agreed) return toast.error("Debes aceptar los términos");
    if (!typedSignature.trim()) return toast.error("Firma escribiendo tu nombre");
    setLoading(true);
    try {
      await api.post(`/events/${event.slug}/submit`, {
        name, email: email || null, phone,
        signature_data: typedSignature, signer_relation: relation,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (sent) return <SuccessCard message="¡Firmado! Gracias por tu consentimiento." />;

  return (
    <form onSubmit={submit} className="mt-8 space-y-6" data-testid="waiver-form">
      <div className="bg-white border border-line rounded-2xl p-6 max-h-96 overflow-auto whitespace-pre-line text-ink-700 leading-relaxed" data-testid="waiver-text-display">
        {cfg.waiver_text}
      </div>
      <NameEmailPhone name={name} setName={setName} email={email} setEmail={setEmail} phone={phone} setPhone={setPhone} />
      {cfg.require_parent_signature && (
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Firmas como</Label>
          <Select value={relation} onValueChange={setRelation}>
            <SelectTrigger className="rounded-xl border-line h-11 mt-1" data-testid="waiver-relation"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="self">Yo mismo/a</SelectItem>
              <SelectItem value="parent">Padre / Madre / Tutor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <label className="flex items-start gap-2">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1" data-testid="waiver-agree" />
        <span className="text-sm text-ink-700">He leído y acepto los términos del consentimiento.</span>
      </label>
      <div>
        <Label className="text-[11px] uppercase tracking-widest text-ink-500">Firma (escribe tu nombre completo)</Label>
        <Input value={typedSignature} onChange={(e) => setTypedSignature(e.target.value)} className="rounded-xl border-line h-12 font-heading text-lg italic mt-1" placeholder="Tu nombre" data-testid="waiver-signature" />
      </div>
      <Button type="submit" disabled={loading} className="rounded-full bg-coral hover:bg-coral-hover text-white h-11 px-6" data-testid="waiver-submit-btn">
        {loading ? "Firmando…" : "Firmar y enviar"}
      </Button>
    </form>
  );
}

// ============== HELPERS ==============
function NameEmailPhone({ name, setName, email, setEmail, phone, setPhone }) {
  return (
    <>
      <div>
        <Label className="text-[11px] uppercase tracking-widest text-ink-500">
          Nombre <span className="text-coral">*</span>
        </Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required className="rounded-xl border-line h-11 mt-1" data-testid="pub-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Correo</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl border-line h-11 mt-1" data-testid="pub-email-input" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-ink-500">Teléfono</Label>
          <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-xl border-line h-11 mt-1" data-testid="pub-phone-input" />
        </div>
      </div>
    </>
  );
}

function PaymentNotice({ event }) {
  const cfg = event.payment_config || {};
  if (cfg.provider === "none" || !cfg.provider) {
    return (
      <div className="text-xs text-ink-500 bg-cream-100 border border-line rounded-xl p-3">
        <b className="text-ink-700">Pago manual:</b>{" "}
        {cfg.manual_instructions || "El organizador te contactará con las instrucciones de pago."}
      </div>
    );
  }
  return (
    <div className="text-xs text-ink-500 bg-cream-100 border border-line rounded-xl p-3">
      <b className="text-ink-700">Pago con {cfg.provider === "stripe" ? "Stripe" : "Mercado Pago"}:</b> los pagos digitales están pendientes de activación. El organizador te contactará.
    </div>
  );
}

function SuccessCard({ message }) {
  return (
    <div className="mt-8 bg-white border border-line rounded-2xl p-6 flex items-start gap-4" data-testid="success-card">
      <CheckCircle2 size={22} strokeWidth={1.75} className="text-sage shrink-0 mt-0.5" />
      <div>
        <h3 className="font-heading text-lg text-ink-900">¡Listo!</h3>
        <p className="mt-1 text-sm text-ink-500">{message}</p>
      </div>
    </div>
  );
}
